<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { object, string } from 'yup';

const { tenant, websiteVariants, seoBacklinkPlatforms, seoBacklink } =
    defineProps({
        tenant: {
            type: Object,
            required: true,
        },
        websiteVariants: {
            type: Array,
            required: true,
        },
        seoBacklinkPlatforms: {
            type: Array,
            required: true,
        },
        seoBacklink: {
            type: Object,
            required: false,
        },
    });

const { route } = useRoute();

const form = reactive({
    websiteVariant: websiteVariants.find(
        ({ id }) => id === seoBacklink?.websiteVariantId,
    ),
    platform: seoBacklink
        ? seoBacklinkPlatforms.find(
              ({ value }) => value === seoBacklink.platform.value,
          )
        : seoBacklinkPlatforms[0],
    sourceUrl: seoBacklink ? seoBacklink.sourceUrl : '',
    date: seoBacklink ? seoBacklink.date : '',
});

const validationSchema = object({
    websiteVariant: object().required('Ce champ est obligatoire.'),
    platform: object().required('Ce champ est obligatoire.'),
    sourceUrl: string().required('Ce champ est obligatoire.'),
    date: string().nullable(),
});

const isSubmitting = ref(false);

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.seo.postBacklink', {
                tenant: tenant.id,
                ...(seoBacklink ? { id: seoBacklink.id } : {}),
            }),
            {
                websiteVariantId: form.websiteVariant?.id,
                platform: form.platform.value,
                sourceUrl: form.sourceUrl,
                date: form.date,
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route('front.projectManagement.seo.netlinking', {
                    tenant: tenant.id,
                }),
            );
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <FormColumn>
            <FormContent>
                <FormGrid :columns="1">
                    <ComboboxField
                        v-model="form.websiteVariant"
                        filtered-option-key="name"
                        :options="websiteVariants"
                        field-name="websiteVariant"
                        field-id="websiteVariant"
                        label="Site"
                        required
                    >
                        <template #optionContent="{ option }">
                            {{ option.name }}
                        </template>
                    </ComboboxField>

                    <ListboxField
                        v-model="form.platform"
                        field-id="platform"
                        field-name="platform"
                        label="Plateforme"
                        :options="seoBacklinkPlatforms"
                        required
                    >
                        <template #buttonContent>
                            {{ form.platform.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>

                    <InputField
                        v-model="form.sourceUrl"
                        label="URL source"
                        field-name="sourceUrl"
                        field-id="sourceUrl"
                        type="url"
                        required
                    />

                    <InputField
                        v-model="form.date"
                        label="Date"
                        field-name="date"
                        field-id="date"
                        type="month"
                    />
                </FormGrid>
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="seoBacklink"
                    :url="
                        route('front.projectManagement.seo.deleteBacklink', {
                            tenant: tenant.id,
                            id: seoBacklink.id,
                        })
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.seo.netlinking',
                                    {
                                        tenant: tenant.id,
                                    },
                                ),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
